import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import BlogCard from "./BlogCard";
import { uiT } from "@/i18n/ui";
import type { SiteLang } from "@/hooks/useLanguage";

interface RelatedPostsProps {
  currentId: string;
  category: string | null;
  lang?: string;
}

const RelatedPosts = ({ currentId, category, lang = "en" }: RelatedPostsProps) => {
  const linkPrefix = lang === "es" ? "/es/blog" : lang === "zh-cn" ? "/zh-cn/blog" : "/en/blog";
  const t = uiT(lang as SiteLang);

  const { data: posts } = useQuery({
    queryKey: ["related-posts", currentId, category, lang],
    queryFn: async () => {
      let query = supabase
        .from("blog_posts")
        .select("id, slug, title, summary, featured_image, category, published_at, author, tags")
        .neq("id", currentId)
        .eq("lang", lang)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(3);

      if (category) {
        query = query.eq("category", category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!posts || posts.length === 0) return null;

  return (
    <section className="py-20 sm:py-28 bg-secondary">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
            {t("keepReading")}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            {t("relatedArticles")}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <BlogCard key={post.id} post={post} index={i} linkPrefix={linkPrefix} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedPosts;
